import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
  Box,
  CSSObject,
  Divider,
  IconButton,
  List,
  Drawer as MuiDrawer,
  Stack,
  Theme,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import useInsertionMenuOptions from "../../hooks/useInsertionMenuOptions";
import LayoutListItem from "./LayoutListItem";

export const DRAWER_WIDTH: number = 240;

const commonMixin = (theme: Theme): CSSObject => ({
  maxHeight: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  bottom: 0,
  borderRadius: 0,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  overflow: "hidden",
  ...commonMixin(theme),
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  overflow: "hidden",
  ...commonMixin(theme),
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  maxHeight: "100%",
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const InsertionDrawer: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isPersistent, setIsPersistent] = useState<boolean>(true);
  const menuOptions = useInsertionMenuOptions();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.up("sm"));

  const onDrawerOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onDrawerClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onClickIsPersistent = useCallback(() => {
    setIsPersistent(!isPersistent);
  }, [isPersistent]);

  const HeaderDrawer = useCallback(() => {
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        sx={{
          height: 50,
          bgcolor: "action.hover",
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={400}
          sx={{ visibility: !isOpen ? "hidden" : undefined }}
        >
          Insert Element
        </Typography>
        <IconButton size="small" edge="end" onClick={onClickIsPersistent}>
          <Tooltip title={`${isPersistent ? "Unpersist" : "Persist"} Menu`}>
            {isPersistent ? (
              <PushPinIcon sx={{ fontSize: 15 }} />
            ) : (
              <PushPinOutlinedIcon sx={{ fontSize: 15 }} />
            )}
          </Tooltip>
        </IconButton>
      </Stack>
    );
  }, [isOpen, isPersistent, onClickIsPersistent]);

  return (
    <Box
      sx={{
        height: 1,
        position: "relative",
        width: (theme) =>
          isPersistent
            ? DRAWER_WIDTH
            : `calc(${theme.spacing(isSmallDevice ? 8 : 7)} + 1px)`,
      }}
    >
      <Drawer
        variant="permanent"
        open={isOpen}
        onMouseEnter={isPersistent ? undefined : onDrawerOpen}
        onMouseLeave={isPersistent ? undefined : onDrawerClose}
      >
        <Stack flexGrow={1} maxHeight={1}>
          <HeaderDrawer />
          <Divider variant="fullWidth" />
          <List
            disablePadding
            dense
            sx={{
              flexGrow: 1,
              mt: 0,
              overflowY: isOpen ? "scroll" : "hidden",
              overflowX: "hidden",
            }}
          >
            {menuOptions.map((option) => (
              <LayoutListItem
                key={option.id}
                isDrawerOpen={isOpen}
                option={option}
                isSelected={option.selected}
                onClick={option.onClick}
              />
            ))}
          </List>
        </Stack>
      </Drawer>
    </Box>
  );
};

export default InsertionDrawer;
